import ApplicationService from 'src/api/application';
import filteringHelper from 'src/services/filteringHelper';
import Vue from 'vue';

const state = {
  status: null,
  messages: '',
  downloadUrl: null,
  error: null,
  formData: null,
  categories: {
    publication: [],
    patent: [],
  },
  source: [],
};

const getters = {
  downloadUrl: (state) => {
    return state.downloadUrl;
  },
  messages: (state) => {
    return state.messages;
  },
  error: (state) => {
    return state.error;
  },
  status: (state) => {
    return state.status;
  },
  categories: (state) => {
    return state.categories;
  },
  source: (state) => {
    return state.source;
  },
};

const actions = {
  buildFormData({ commit, rootGetters, dispatch }, payload) {
    // Payload is coming from the export modal form
    if (payload.options) {
      // Update messaging when new export is started
      dispatch('clearMessages');
      dispatch('messageHandler', { message: 'Initializing export...' });
      // Clear previous export form data
      commit('updateFormData', null);
      // Update source
      commit('updateSource', payload.source);

      // fetch scan data from scans module
      const { query, version_id, name, created_date, query_elements } =
        rootGetters['scans/getScanDetails'];
      // Get product name from appConfig module
      const { product } = rootGetters['app/appConfig'];
      // Get scan type from scans module
      const scanType = rootGetters['scans/scanType'];

      // Build form data object
      let formdata = {};
      formdata.source = payload.source;
      formdata.options = payload.options; // Summary Report, Data Export, or both
      formdata.zip_name = payload.zip_name; // File name user entered
      formdata.scan_id = version_id;
      formdata.scan_name = name;
      formdata.scan_type = scanType;
      formdata.scan_search_query = query_elements.semantic.length
        ? query_elements.semantic[0]
        : query_elements.boolean.length
        ? query_elements.boolean[0].value
        : name; // Get full value of query if available so not truncated in export slides
      formdata.scan_date = created_date ? created_date : null;
      formdata.query_type = query_elements.semantic.length
        ? 'Semantic'
        : 'Keyword';
      formdata.productName = product.name;

      // Build publication data
      if (payload.publication) {
        // Update publication categories
        commit('updateCategories', {
          source: 'publication',
          data: payload.publication.categories,
        });

        // Get publication current filtering state
        const publicationFilters =
          rootGetters['filters/currentFiltering']['publication'] || null;
        // Add publication filters to payload
        payload.publication.filters = publicationFilters;
        // Add query to payload
        payload.publication.query = query;

        // Update query based on scan type
        if (scanType !== 'semantic') {
          if (publicationFilters && publicationFilters.search_within_results) {
            // We have search within results, call helper function to add search within results to query
            payload.publication.query = filteringHelper.filterResultsText(
              query,
              publicationFilters.search_within_results
            );
          }
        } else {
          if (publicationFilters && publicationFilters.positive) {
            // Create positive and negative arrays to add to query
            const positive = publicationFilters.positive || [];
            const negative = publicationFilters.negative || [];

            if (positive.length || negative.length) {
              // Call helper function to add positive and negative arrays to query
              payload.publication.query = filteringHelper.filterSemantic(
                query,
                {
                  positive,
                  negative,
                }
              );
            }
          }
        }

        // Add leadersBy to payload
        payload.publication.leadersBy = payload.publication.categories.includes(
          'leaders'
        )
          ? rootGetters['leaders/leadersBy'].publication
          : null;

        // Add publication data to form data
        formdata.publication = payload.publication;
      }

      // Build patent data
      if (payload.patent) {
        // Update patent categories
        commit('updateCategories', {
          source: 'patent',
          data: payload.patent.categories,
        });

        // Get patent current filtering state
        const patentFilters =
          rootGetters['filters/currentFiltering']['patent'] || null;
        // Add patent filters to payload
        payload.patent.filters = patentFilters;
        // Add query to payload
        payload.patent.query = query;

        // Update query based on scan type
        if (scanType !== 'semantic') {
          if (patentFilters && patentFilters.search_within_results) {
            // We have search within results, call helper function to add search within results to query
            payload.patent.query = filteringHelper.filterResultsText(
              query,
              patentFilters.search_within_results
            );
          }
        } else {
          if (patentFilters && patentFilters.positive) {
            // Create positive and negative arrays to add to query
            const positive = patentFilters.positive || [];
            const negative = patentFilters.negative || [];

            if (positive.length || negative.length) {
              // Call helper function to add positive and negative arrays to query
              payload.patent.query = filteringHelper.filterSemantic(query, {
                positive,
                negative,
              });
            }
          }
        }

        // Add leadersBy to payload
        payload.patent.leadersBy = payload.patent.categories.includes('leaders')
          ? rootGetters['leaders/leadersBy'].patent
          : null;

        // Add patent data to form data
        formdata.patent = payload.patent;
      }

      // Update form data state with new form data
      commit('updateFormData', formdata);

      // Check if there are non-document categories selected. If so, we need to wait for the data to come in from the DownloadBanner for those categories before kicking off the export.
      const categoryQueue = payload.source
        .map((source) => {
          return payload[source].categories.filter(
            (category) => category !== 'documents'
          );
        })
        .flat();
      // If we aren't waiting for non-document category data, kick off export
      if (!categoryQueue.length) {
        dispatch('initializeExport');
      }
    } else {
      // Payload is coming from the Download Banner containing image and non-documents data - update form data and kick off export

      // Add doc count here as documents may not have returned when export initiated. The service will add the doc count when fetching documents if it is not present in the request payload.
      state.source.forEach((source) => {
        // Get pagination for source
        const pagination = rootGetters['documents/getPagination'][source];
        // Get formdata for source
        const sourceData = JSON.parse(state.formData.get(source));
        // Add doc count
        sourceData.scan_doc_count = pagination.rowsNumber;
        // Update payload with new data for source
        payload[source] = sourceData;
      });
      commit('updateFormData', payload);
      dispatch('initializeExport');
    }
  },
  async initializeExport({ commit, rootGetters, dispatch, state }) {
    // Clear categories when export is initialized - categories are used in the DownloadBanner to track if we need to get additional data
    commit('updateCategories');
    const { version_id } = rootGetters['scans/getScanDetails'];
    const applicationService = new ApplicationService();
    const response = await applicationService.initializeExport(state.formData);

    if (response && response.taskId) {
      // send task id to the message broker for tracking
      dispatch('io/addTask', response.taskId, { root: true });

      // used for component level comparisons and tracking for the scan
      let task_dict =
        JSON.parse(localStorage.getItem('task_dict') || '{}') || {};
      task_dict[version_id] = response.taskId;
      localStorage.setItem('task_dict', JSON.stringify(task_dict));
    } else {
      dispatch('messageHandler', {
        message: 'Something went wrong with the download',
        error: true,
      });
    }
  },
  /* handles messages related to the export
  if you don't need the taskId, dispatch action to the io module to prune the task from the list */
  messageHandler({ commit, dispatch }, payload) {
    if (payload) {
      if (payload.error) {
        commit('setError', payload.error);
        dispatch('io/removeTask', payload.taskId, { root: true });
      }
      if (payload.message) {
        commit('setMessage', payload.message);
      }
      if (payload.data && payload.data.downloadUrl) {
        commit('setDownloadUrl', payload.data.downloadUrl);
        dispatch('io/removeTask', payload.taskId, { root: true });
      }
    }
  },
  clearMessages({ commit }) {
    commit('clearMessages');
  },
};

// mutations
const mutations = {
  setDownloadUrl(state, payload) {
    state.downloadUrl = payload;
  },
  setMessage(state, payload) {
    state.messages = payload;
  },
  clearMessages(state) {
    state.messages = '';
    state.downloadUrl = null;
    state.error = null;
    state.status = null;
  },
  setError(state, payload) {
    state.error = payload;
  },
  updateSource(state, payload) {
    state.source = payload;
  },
  updateCategories(state, payload) {
    // If no payload, clear categories
    if (!payload) {
      state.categories = {
        publication: [],
        patent: [],
      };
    } else {
      // If payload, update categories state using Vue.set to ensure reactivity
      Vue.set(state.categories, payload.source, payload.data);
    }
  },
  updateFormData(state, payload) {
    // If no payload or no form data object in state, create a new form data object
    if (!payload || !state.formData) state.formData = new FormData();
    // Loop through payload and add to form data
    if (payload) {
      for (const [key, value] of Object.entries(payload)) {
        let _value = null;
        // Replace single quotes and apostrophes with double quotes, remove escape characters
        if (Array.isArray(value)) {
          const val = value.map((item) => {
            if (typeof item === 'string') {
              return item.replace(/'/g, "''").replace(/\\/g, '');
            }
            return item;
          });
          _value = JSON.stringify(val);
        } else if (value instanceof File || typeof value === 'string') {
          _value = value;
        } else {
          _value = JSON.stringify(value, (key, val) => {
            // replace single quotes and apostrophes with double quotes, remove escape characters
            if (typeof val === 'string')
              return val.replace(/'/g, "''").replace(/\\/g, '');
            return val;
          });
        }
        state.formData.set(key, _value);
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
